<script setup>
import { computed, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TemplatePreview from 'dashboard/components-next/template-preview/TemplatePreview.vue';
import { PLATFORMS } from 'dashboard/services/TemplateConstants';
import {
  checkFileSizeLimit,
  resolveMaximumFileUploadSize,
} from 'shared/helpers/FileHelper';
import channelTemplatePresets from 'custom/api/channelTemplatePresets';
import {
  initialMappings,
  mediaHeader,
  previewVariables,
  serializeMappings,
  templateKey,
  templateWithHeaderPreview,
  templateWithMediaPreview,
} from 'custom/helpers/channelTemplatePresets';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();
const store = useStore();
const enabled = ref(false);
const loading = ref(true);
const saving = ref(false);
const deleting = ref(false);
const templates = shallowRef([]);
const presets = ref([]);
const selectedIdentity = ref('');
const mappings = ref([]);
const mediaFile = ref(null);
const mediaPreviewUrl = ref('');
const removeDefaultMedia = ref(false);

const catalogEntries = computed(() => {
  const entries = [...templates.value];
  presets.value.forEach(preset => {
    const exists = entries.some(
      template =>
        String(templateKey(template)) === preset.template_key &&
        template.language.toLowerCase() === preset.language.toLowerCase()
    );
    if (!exists) {
      entries.push({
        id: preset.template_key,
        name: preset.template_name,
        language: preset.language,
        status: 'UNAVAILABLE',
        components: [],
        presetOnly: true,
      });
    }
  });
  return entries;
});

const identityFor = template =>
  `${templateKey(template)}::${template.language || ''}`;

const selectedTemplate = computed(() =>
  catalogEntries.value.find(
    template => identityFor(template) === selectedIdentity.value
  )
);

const selectedPreset = computed(() => {
  const template = selectedTemplate.value;
  if (!template) return null;
  return presets.value.find(
    preset =>
      preset.template_key === String(templateKey(template)) &&
      preset.language.toLowerCase() === template.language.toLowerCase()
  );
});

const statusLabel = computed(() => {
  const status = selectedPreset.value?.status || 'not_configured';
  const labels = {
    ready: t('CHANNEL_TEMPLATE_PRESETS.STATUS.ready'),
    not_configured: t('CHANNEL_TEMPLATE_PRESETS.STATUS.not_configured'),
    review_required: t('CHANNEL_TEMPLATE_PRESETS.STATUS.review_required'),
    template_unavailable: t(
      'CHANNEL_TEMPLATE_PRESETS.STATUS.template_unavailable'
    ),
    fallback: t('CHANNEL_TEMPLATE_PRESETS.STATUS.fallback'),
  };
  return labels[status] || labels.not_configured;
});

const sourceOptions = computed(() => [
  ['manual', t('CHANNEL_TEMPLATE_PRESETS.SOURCES.MANUAL')],
  ['fixed_text', t('CHANNEL_TEMPLATE_PRESETS.SOURCES.FIXED_TEXT')],
  ['contact_name', t('CHANNEL_TEMPLATE_PRESETS.SOURCES.CONTACT_NAME')],
  ['contact_email', t('CHANNEL_TEMPLATE_PRESETS.SOURCES.CONTACT_EMAIL')],
  ['contact_phone_number', t('CHANNEL_TEMPLATE_PRESETS.SOURCES.CONTACT_PHONE')],
  [
    'contact_custom_attribute',
    t('CHANNEL_TEMPLATE_PRESETS.SOURCES.CONTACT_ATTRIBUTE'),
  ],
  ['conversation_id', t('CHANNEL_TEMPLATE_PRESETS.SOURCES.CONVERSATION_ID')],
  [
    'conversation_custom_attribute',
    t('CHANNEL_TEMPLATE_PRESETS.SOURCES.CONVERSATION_ATTRIBUTE'),
  ],
]);

const previewParameters = computed(() => {
  const body = {};
  const header = {};
  mappings.value.forEach(mapping => {
    const preview =
      mapping.source === 'fixed_text'
        ? mapping.fixedValue
        : `[${sourceOptions.value.find(option => option[0] === mapping.source)?.[1] || mapping.key}]`;
    const [section, key] = mapping.path.split('.');
    if (section === 'body') body[key] = preview;
    if (section === 'header') header[key] = preview;
  });
  return { body, header };
});

const mappingPreview = computed(() =>
  previewVariables(previewParameters.value)
);

const previewTemplate = computed(() => {
  if (!selectedTemplate.value) return null;
  const withMedia = templateWithMediaPreview(
    selectedTemplate.value,
    mediaPreviewUrl.value
  );
  return templateWithHeaderPreview(withMedia, previewParameters.value.header);
});

const hasMediaHeader = computed(() => !!mediaHeader(selectedTemplate.value));

const resetForm = () => {
  const preset = selectedPreset.value;
  mappings.value = selectedTemplate.value
    ? initialMappings(selectedTemplate.value, preset?.configuration)
    : [];
  mediaFile.value = null;
  mediaPreviewUrl.value = preset?.default_media?.url || '';
  removeDefaultMedia.value = false;
};

watch(selectedIdentity, resetForm);
watch(presets, resetForm, { deep: true });
watch(
  catalogEntries,
  entries => {
    const selectionExists = entries.some(
      entry => identityFor(entry) === selectedIdentity.value
    );
    if (!selectionExists) {
      selectedIdentity.value = entries[0] ? identityFor(entries[0]) : '';
    }
  },
  { immediate: true }
);

const loadPresets = async inboxId => {
  loading.value = true;
  try {
    const { data } = await channelTemplatePresets.list(inboxId);
    if (inboxId !== props.inbox.id) return;
    enabled.value = data.enabled;
    templates.value = data.templates || [];
    presets.value = data.presets || [];
  } catch (error) {
    if (inboxId !== props.inbox.id) return;
    enabled.value = false;
    templates.value = [];
    presets.value = [];
  } finally {
    if (inboxId === props.inbox.id) loading.value = false;
  }
};

const validateMedia = file => {
  const limit = resolveMaximumFileUploadSize(
    store.getters['globalConfig/get']?.maximumFileUploadSize
  );
  if (!checkFileSizeLimit(file, limit)) {
    useAlert(t('CHANNEL_TEMPLATE_PRESETS.FILE_TOO_LARGE', { limit }));
    return false;
  }

  const format = mediaHeader(selectedTemplate.value)?.format;
  const compatible =
    (format === 'IMAGE' && file.type.startsWith('image/')) ||
    (format === 'VIDEO' && file.type.startsWith('video/')) ||
    (format === 'DOCUMENT' && !file.type.match(/^(image|video|audio)\//));
  if (!compatible) useAlert(t('CHANNEL_TEMPLATE_PRESETS.FILE_TYPE_ERROR'));
  return compatible;
};

const onMediaSelected = event => {
  const [file] = event.target.files || [];
  if (!file || !validateMedia(file)) return;
  mediaFile.value = file;
  mediaPreviewUrl.value = URL.createObjectURL(file);
  removeDefaultMedia.value = false;
};

const clearMedia = () => {
  mediaFile.value = null;
  mediaPreviewUrl.value = '';
  removeDefaultMedia.value = true;
};

const save = async () => {
  if (!selectedTemplate.value || selectedTemplate.value.presetOnly) return;
  saving.value = true;
  const payload = new FormData();
  payload.append('language', selectedTemplate.value.language);
  payload.append(
    'configuration',
    JSON.stringify({ variables: serializeMappings(mappings.value) })
  );
  if (mediaFile.value) payload.append('default_media', mediaFile.value);
  if (removeDefaultMedia.value) payload.append('remove_default_media', 'true');

  try {
    const { data } = await channelTemplatePresets.upsert(
      props.inbox.id,
      templateKey(selectedTemplate.value),
      payload
    );
    presets.value = [
      ...presets.value.filter(preset => preset.id !== data.id),
      data,
    ];
    useAlert(t('CHANNEL_TEMPLATE_PRESETS.SAVE_SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('CHANNEL_TEMPLATE_PRESETS.SAVE_ERROR')
    );
  } finally {
    saving.value = false;
  }
};

const remove = async () => {
  if (!selectedPreset.value || !selectedTemplate.value) return;
  deleting.value = true;
  try {
    await channelTemplatePresets.remove(
      props.inbox.id,
      selectedPreset.value.template_key,
      selectedPreset.value.language
    );
    presets.value = presets.value.filter(
      preset => preset.id !== selectedPreset.value.id
    );
    useAlert(t('CHANNEL_TEMPLATE_PRESETS.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CHANNEL_TEMPLATE_PRESETS.DELETE_ERROR'));
  } finally {
    deleting.value = false;
  }
};

watch(
  () => props.inbox.id,
  inboxId => {
    selectedIdentity.value = '';
    templates.value = [];
    presets.value = [];
    loadPresets(inboxId);
  },
  { immediate: true }
);
</script>

<template>
  <div>
    <section
      v-if="enabled || loading"
      class="w-full rounded-xl border border-n-weak bg-n-solid-1 p-5 lg:p-6"
    >
      <div
        class="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between"
      >
        <div>
          <h3 class="text-base font-semibold text-n-slate-12">
            {{ t('CHANNEL_TEMPLATE_PRESETS.SETTINGS_TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ t('CHANNEL_TEMPLATE_PRESETS.SETTINGS_DESCRIPTION') }}
          </p>
        </div>
        <span
          v-if="selectedTemplate"
          class="w-fit rounded-full bg-n-alpha-2 px-3 py-1 text-xs font-medium text-n-slate-12"
        >
          {{ statusLabel }}
        </span>
      </div>

      <div v-if="loading" class="py-10 text-center text-sm text-n-slate-11">
        {{ t('CHANNEL_TEMPLATE_PRESETS.LOADING') }}
      </div>

      <div v-else-if="!catalogEntries.length" class="py-10 text-center">
        <p class="text-sm text-n-slate-11">
          {{ t('CHANNEL_TEMPLATE_PRESETS.EMPTY') }}
        </p>
      </div>

      <template v-else>
        <label class="mt-6 block text-sm font-medium text-n-slate-12">
          {{ t('CHANNEL_TEMPLATE_PRESETS.SELECT_TEMPLATE') }}
          <select
            v-model="selectedIdentity"
            class="mt-2 h-10 w-full rounded-lg border border-n-weak bg-n-alpha-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
          >
            <option
              v-for="template in catalogEntries"
              :key="identityFor(template)"
              :value="identityFor(template)"
            >
              {{ template.name
              }}{{ t('CHANNEL_TEMPLATE_PRESETS.IDENTITY_SEPARATOR')
              }}{{ template.language }}
            </option>
          </select>
        </label>

        <div
          v-if="selectedTemplate"
          class="mt-6 grid items-start gap-6 lg:grid-cols-[minmax(0,7fr)_minmax(20rem,5fr)]"
        >
          <div class="flex min-w-0 flex-col gap-6">
            <section
              v-if="hasMediaHeader"
              class="rounded-xl border border-n-weak p-4"
            >
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ t('CHANNEL_TEMPLATE_PRESETS.DEFAULT_MEDIA') }}
              </h4>
              <p class="mt-1 text-sm text-n-slate-11">
                {{ t('CHANNEL_TEMPLATE_PRESETS.DEFAULT_MEDIA_HELP') }}
              </p>
              <input
                class="mt-4 block w-full text-sm text-n-slate-12 file:mr-3 file:rounded-lg file:border-0 file:bg-n-alpha-2 file:px-3 file:py-2 file:text-sm file:text-n-slate-12"
                type="file"
                @change="onMediaSelected"
              />
              <div
                v-if="mediaPreviewUrl"
                class="mt-3 flex items-center justify-between gap-3 rounded-lg bg-n-alpha-2 p-3"
              >
                <span class="min-w-0 truncate text-sm text-n-slate-12">
                  {{
                    mediaFile?.name || selectedPreset?.default_media?.filename
                  }}
                </span>
                <button
                  type="button"
                  class="shrink-0 text-sm font-medium text-n-ruby-11 hover:underline"
                  @click="clearMedia"
                >
                  {{ t('CHANNEL_TEMPLATE_PRESETS.REMOVE_MEDIA') }}
                </button>
              </div>
            </section>

            <section class="rounded-xl border border-n-weak p-4">
              <h4 class="text-sm font-semibold text-n-slate-12">
                {{ t('CHANNEL_TEMPLATE_PRESETS.VARIABLES') }}
              </h4>
              <p v-if="!mappings.length" class="mt-3 text-sm text-n-slate-11">
                {{ t('CHANNEL_TEMPLATE_PRESETS.NO_VARIABLES') }}
              </p>
              <div
                v-for="mapping in mappings"
                :key="mapping.path"
                class="mt-4 grid gap-3 rounded-lg bg-n-alpha-1 p-3 sm:grid-cols-[minmax(8rem,1fr)_minmax(12rem,2fr)]"
              >
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    {{ mapping.section
                    }}{{ t('CHANNEL_TEMPLATE_PRESETS.FIELD_SEPARATOR')
                    }}{{ mapping.key }}
                  </p>
                  <p class="mt-1 text-xs text-n-slate-11">{{ mapping.path }}</p>
                </div>
                <div class="grid gap-2">
                  <select
                    v-model="mapping.source"
                    class="h-10 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  >
                    <option
                      v-for="option in sourceOptions"
                      :key="option[0]"
                      :value="option[0]"
                    >
                      {{ option[1] }}
                    </option>
                  </select>
                  <input
                    v-if="mapping.source === 'fixed_text'"
                    v-model="mapping.fixedValue"
                    type="text"
                    :placeholder="t('CHANNEL_TEMPLATE_PRESETS.FIXED_VALUE')"
                    class="h-10 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  />
                  <input
                    v-if="mapping.source.includes('custom_attribute')"
                    v-model="mapping.keyValue"
                    type="text"
                    :placeholder="t('CHANNEL_TEMPLATE_PRESETS.ATTRIBUTE_KEY')"
                    class="h-10 w-full rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  />
                </div>
              </div>
            </section>
          </div>

          <aside
            class="min-w-0 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
          >
            <h4 class="text-sm font-semibold text-n-slate-12">
              {{ t('CHANNEL_TEMPLATE_PRESETS.PREVIEW') }}
            </h4>
            <div
              v-if="selectedTemplate.presetOnly"
              class="mt-4 rounded-lg bg-n-amber-3 p-4 text-sm text-n-amber-11"
            >
              {{ t('CHANNEL_TEMPLATE_PRESETS.UNAVAILABLE_HELP') }}
            </div>
            <div v-else class="mt-4 flex justify-center">
              <TemplatePreview
                :template="previewTemplate"
                :variables="mappingPreview"
                :platform="PLATFORMS.WHATSAPP"
              />
            </div>
          </aside>
        </div>

        <div
          v-if="selectedTemplate"
          class="mt-6 flex flex-col-reverse gap-3 border-t border-n-weak pt-5 sm:flex-row sm:items-center sm:justify-between"
        >
          <NextButton
            v-if="selectedPreset"
            ruby
            faded
            :label="t('CHANNEL_TEMPLATE_PRESETS.DELETE')"
            :is-loading="deleting"
            @click="remove"
          />
          <span v-else />
          <NextButton
            v-if="!selectedTemplate.presetOnly"
            :label="t('CHANNEL_TEMPLATE_PRESETS.SAVE')"
            :is-loading="saving"
            @click="save"
          />
        </div>
      </template>
    </section>
  </div>
</template>
